import React, { useEffect, useState, useCallback, useMemo } from "react";
import { apiGet, apiPost } from "../../services/api";
import { safeDate } from "../../utils/dateUtils";
import { useToast } from "../common/ToastProvider";
import Button from "../common/Button";
import "./VisitList.css";

interface Visit {
  visit_id: string | number;
  visit_date: string;
  visit_type: string;
  remarks?: string;
  status?: string;
  has_records?: boolean;
  has_prescriptions?: boolean;
  has_pending?: boolean;
  created_at?: string;
}

interface VisitListProps {
  patientId: string | number | null;
  onSelectVisit: (visit: Visit) => void;
  activeVisitId?: string | number | null;
}

interface VisitCardProps {
  visit: Visit;
  isActive: boolean;
  onClick: () => void;
}

const formatVisitDate = (dateString: string) => {
  const date = safeDate(dateString);
  if (!date) return null;
  return {
    day: date.getDate(),
    month: date.toLocaleDateString("en-US", { month: "short" }),
    weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
    year: date.getFullYear(),
  };
};

function normalizeVisit(v: Record<string, unknown>): Visit {
  return {
    visit_id: (v.visit_id ?? v.visitid) as number,
    visit_date: (v.visit_date ?? v.visitdate) as string,
    visit_type: (v.visit_type ?? "Checkup") as string,
    remarks: (v.remarks ?? "") as string,
    status: (v.status ?? "Open") as string,
    has_records: !!v.has_records,
    has_prescriptions: !!v.has_prescriptions,
    has_pending: !!v.has_pending,
    created_at: v.created_at as string | undefined,
  };
}

function VisitCard({ visit, isActive, onClick }: VisitCardProps) {
  const dateObj = formatVisitDate(visit.visit_date);
  const norm = String(visit.status ?? "").trim().toUpperCase().replace(/\s+/g, "_");
  const isLocked = norm.includes("LOCK");
  const dayLabel = dateObj?.day ?? "—";
  const monthLabel = dateObj?.month ?? "—";

  const handleClick = () => {
    if (isLocked) return;
    onClick();
  };

  return (
    <div
      className={`visit-card ${isActive ? "active" : ""} ${
        isLocked ? "locked" : ""
      }`}
      onClick={handleClick}
      title={isLocked ? "This visit is locked" : "Open visit"}
      role="button"
      aria-disabled={isLocked}
      tabIndex={isLocked ? -1 : 0}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (!isLocked && (e.key === "Enter" || e.key === " ")) {
          handleClick();
        }
      }}
    >
      <div className="vc-date">
        <span className="vc-day">{dayLabel}</span>
        <span className="vc-month">{monthLabel}</span>
      </div>

      <div className="vc-info">
        <div className="vc-top">
          <span className="vc-type">{visit.visit_type}</span>
          {isLocked && <span className="icon-lock">🔒</span>}
        </div>

        <div className="vc-indicators">
          {visit.has_records && (
            <span
              className="ind-pill note"
              title="Clinical Notes"
              aria-label="Has clinical notes"
            >
              📝
            </span>
          )}

          {visit.has_prescriptions && (
            <span
              className="ind-pill rx"
              title="Prescription"
              aria-label="Has prescriptions"
            >
              💊
            </span>
          )}

          {visit.has_pending && (
            <span
              className="ind-pill pending"
              title="Pending Items"
              aria-label="Has pending procedures"
            >
              ⏳
            </span>
          )}
        </div>
      </div>

      <div className="vc-action">
        <span className="arrow">›</span>
      </div>
    </div>
  );
}

export default function VisitList({ patientId, onSelectVisit, activeVisitId }: VisitListProps) {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  const loadVisits = useCallback(async () => {
    if (!patientId) {
      setVisits([]);
      return;
    }

    setLoading(true);
    try {
      const data = await apiGet(`/api/visits/patient/${patientId}`) as Record<string, unknown>[];
      const normalized = (data || []).map(normalizeVisit);

      normalized.sort(
        (a, b) => (safeDate(b.visit_date)?.getTime() ?? 0) - (safeDate(a.visit_date)?.getTime() ?? 0)
      );

      setVisits(normalized);
    } catch (err) {
      console.error("Error loading visits:", err);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  const handleAddToday = async () => {
    if (!patientId || loading) return;

    try {
      const result = await apiPost(`/api/visits/today/${patientId}`, {}) as Record<string, unknown> | undefined;
      await loadVisits();

      if (result) onSelectVisit(normalizeVisit(result));
    } catch (err) {
      showToast({ message: "Could not create visit. It might already exist.", type: "error" });
    }
  };

  const grouped = useMemo(() => {
    const todayStr = new Date().toDateString();
    const groups = { today: [] as Visit[], history: [] as Visit[] };

    visits.forEach((v) => {
      const vDate = safeDate(v.visit_date)?.toDateString();
      if (vDate === todayStr) groups.today.push(v);
      else groups.history.push(v);
    });

    return groups;
  }, [visits]);

  if (!patientId) return null;

  return (
    <div className="visit-list-container">
      <div className="vl-header">
        <div className="vl-title">
          <h4>Visits</h4>
          <span className="count-badge">{visits.length}</span>
        </div>

        <Button
          variant="primary"
          onClick={handleAddToday}
          disabled={loading}
          title={loading ? "Please wait…" : "Create today's visit"}
        >
          + New Visit
        </Button>
      </div>

      <div className="vl-scroll-area">
        {loading ? (
          <div className="vl-loading">Syncing history...</div>
        ) : visits.length === 0 ? (
          <div className="vl-empty">
            <div className="empty-icon">📅</div>
            <p>No visit history</p>
            <small>Start a new visit to begin.</small>
          </div>
        ) : (
          <>
            {grouped.today.length > 0 && (
              <div className="vl-group">
                <div className="group-label">Today</div>
                {grouped.today.map((v) => (
                  <VisitCard
                    key={v.visit_id}
                    visit={v}
                    isActive={activeVisitId === v.visit_id}
                    onClick={() => onSelectVisit(v)}
                  />
                ))}
              </div>
            )}

            {grouped.history.length > 0 && (
              <div className="vl-group">
                <div className="group-label">Previous History</div>
                {grouped.history.map((v) => (
                  <VisitCard
                    key={v.visit_id}
                    visit={v}
                    isActive={activeVisitId === v.visit_id}
                    onClick={() => onSelectVisit(v)}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
